import type { JSX } from 'react';
import { useState } from 'react';
import { Modal, List, Button, Popconfirm, message, Spin, Tag } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { editorApi, type WikiDiffChange } from '../lib/wikiApi';

/**
 * История версий статьи/стандарта (Q45 + версионирование стандартов FISHKA
 * HUB). Версии пишутся при каждом сохранении через редактор. Версии
 * показываются как semver (v{N}.0). Diff — структурный построчный с типами
 * ADD/DEL/MOD (считается на бэке через LCS, без внешних библиотек):
 * ADD зелёный, DEL красный (зачёркнутый), MOD оранжевый.
 */
export function VersionHistory({
  articleId,
  open,
  onClose,
  onRolledBack,
}: {
  articleId: string;
  open: boolean;
  onClose: () => void;
  onRolledBack: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<number | null>(null);

  const versionsQuery = useQuery({
    queryKey: ['wiki', 'versions', articleId],
    queryFn: () => editorApi.listVersions(articleId),
    enabled: open,
  });

  const diffQuery = useQuery({
    queryKey: ['wiki', 'version-diff', articleId, selected],
    queryFn: () => editorApi.versionDiff(articleId, selected!),
    enabled: open && selected != null,
  });

  const rollbackMut = useMutation({
    mutationFn: (version: number) => editorApi.rollback(articleId, version),
    onSuccess: () => {
      message.success('Откат выполнен — статья восстановлена');
      qc.invalidateQueries({ queryKey: ['wiki', 'versions', articleId] });
      onRolledBack();
      onClose();
    },
    onError: (e: Error) => message.error(e.message),
  });

  return (
    <Modal title="История версий" open={open} onCancel={onClose} footer={null} width={760}>
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16, minHeight: 320 }}>
        {/* Список версий */}
        <div style={{ borderRight: '1px solid var(--border)', paddingRight: 12, maxHeight: 420, overflowY: 'auto' }}>
          {versionsQuery.isLoading && <Spin />}
          {!versionsQuery.isLoading && (versionsQuery.data?.length ?? 0) === 0 && (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              Пока нет сохранённых версий. Они появляются при каждом редактировании.
            </div>
          )}
          <List
            size="small"
            dataSource={versionsQuery.data ?? []}
            renderItem={(v) => (
              <List.Item
                onClick={() => setSelected(v.version)}
                style={{
                  cursor: 'pointer',
                  background: selected === v.version ? 'rgba(255,105,0,0.08)' : 'transparent',
                  padding: '6px 8px',
                  borderRadius: 6,
                }}
              >
                <div style={{ width: '100%' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontFamily: 'monospace', color: 'var(--fk-orange)' }}>{v.label}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {new Date(v.createdAt).toLocaleString('ru-RU')} · {v.authorName || '—'}
                  </div>
                  {v.summary && (
                    <div style={{ fontSize: 12, marginTop: 4, color: 'var(--text, #251D4A)' }}>
                      Изменено: {v.summary}
                    </div>
                  )}
                  {v.effectiveFrom && (
                    <Tag color="blue" style={{ marginTop: 4, fontSize: 11 }}>
                      действует с {fmtDate(v.effectiveFrom)}
                    </Tag>
                  )}
                </div>
              </List.Item>
            )}
          />
        </div>

        {/* Diff + откат */}
        <div>
          {selected == null && (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, paddingTop: 40, textAlign: 'center' }}>
              Выберите версию слева, чтобы увидеть отличия от текущей.
            </div>
          )}
          {selected != null && diffQuery.isLoading && <Spin />}
          {selected != null && diffQuery.data && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Отличия {diffQuery.data.label} → текущая
                </span>
                <Popconfirm
                  title={`Откатить к версии ${diffQuery.data.label}?`}
                  description="Текущее состояние сохранится как новая версия."
                  okText="Откатить"
                  cancelText="Отмена"
                  onConfirm={() => rollbackMut.mutate(selected)}
                >
                  <Button size="small" danger loading={rollbackMut.isPending}>
                    Откатить к {diffQuery.data.label}
                  </Button>
                </Popconfirm>
              </div>
              <DiffSummary summary={diffQuery.data.summary} />
              <StructuralDiff changes={diffQuery.data.changes} />
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** Сводка изменений: +N добавлено / −N удалено / ~N изменено. */
export function DiffSummary({
  summary,
}: {
  summary: { added: number; deleted: number; modified: number };
}): JSX.Element {
  const { added, deleted, modified } = summary;
  if (added + deleted + modified === 0) {
    return <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 8 }}>Текст не отличается.</div>;
  }
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: 12, fontWeight: 600 }}>
      {added > 0 && <span style={{ color: DIFF_META.ADD.fg }}>+{added} добавлено</span>}
      {deleted > 0 && <span style={{ color: DIFF_META.DEL.fg }}>−{deleted} удалено</span>}
      {modified > 0 && <span style={{ color: DIFF_META.MOD.fg }}>~{modified} изменено</span>}
    </div>
  );
}

/** Цвета diff по типам (как в ките «LMS Система»): ADD/DEL/MOD. */
const DIFF_META: Record<WikiDiffChange['type'], { fg: string; bg: string; sign: string }> = {
  ADD: { fg: '#5C8A24', bg: 'rgba(124,179,66,.10)', sign: '+' },
  DEL: { fg: '#C62828', bg: 'rgba(229,57,53,.08)', sign: '−' },
  MOD: { fg: '#D9580E', bg: 'rgba(255,105,0,.08)', sign: '~' },
};

/** Структурный построчный diff с цветовой кодировкой ADD/DEL/MOD. */
export function StructuralDiff({ changes }: { changes: WikiDiffChange[] }): JSX.Element {
  if (changes.length === 0) {
    return <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Текст не отличается.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 360, overflowY: 'auto' }}>
      {changes.map((ch, i) => {
        const d = DIFF_META[ch.type];
        return (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 9,
              padding: '8px 11px',
              borderRadius: 6,
              background: d.bg,
              borderLeft: `3px solid ${d.fg}`,
            }}
          >
            <span style={{ fontWeight: 800, color: d.fg, fontFamily: 'monospace', flexShrink: 0 }}>{d.sign}</span>
            <div style={{ minWidth: 0, fontSize: 13, lineHeight: 1.4 }}>
              {ch.type === 'MOD' && ch.textOld != null && (
                <div style={{ color: DIFF_META.DEL.fg, textDecoration: 'line-through', wordBreak: 'break-word', opacity: 0.85 }}>
                  {ch.textOld}
                </div>
              )}
              <div
                style={{
                  color: 'var(--text)',
                  textDecoration: ch.type === 'DEL' ? 'line-through' : 'none',
                  wordBreak: 'break-word',
                }}
              >
                {ch.text}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}
